// src/pages/VehicleV2/VehicleSaleAndScrapDoc.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaFolderOpen } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/vehicleSalesorScrapReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

export default function VehicleSaleAndScrapDoc() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const saleScrapId = useMemo(() => {
    const fromQuery =
      toInt(searchParams.get("saleScrapId")) ||
      toInt(searchParams.get("saleorscrapId")) ||
      toInt(searchParams.get("saleorScrapId"));
    const fromState = toInt(location?.state?.saleScrapId);
    return fromQuery || fromState || 0;
  }, [searchParams, location?.state]);

  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [message, setMessage] = useState("");

  const loadDocs = async () => {
    if (!saleScrapId) {
      setDocs([]);
      setMessage("Invalid sale/scrap id");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/doc`, {
        params: { saleScrapId, saleorscrapId: saleScrapId },
      });
      if (!data?.success) {
        setDocs([]);
        setMessage(data?.message || "Failed to load documents");
        toast.error(data?.message || "Failed to load documents");
        return;
      }
      if (!data.docs?.length) {
        setDocs([]);
        setMessage(data.message || "There is no Documents for this Vehicle..");
        toast.info(data.message || "There is no Documents for this Vehicle..");
      } else {
        setDocs(data.docs);
        setMessage("");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load documents";
      setDocs([]);
      setMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocs();
  }, [saleScrapId]);

  const openDoc = (doc) => {
    if (!doc?.url) return;
    window.open(doc.url, "_blank", "noopener,noreferrer");
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">VEHICLE SALE / SCRAP DOCUMENTS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />
        <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-xl md:text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>Uploaded Documents</h2>
              {saleScrapId ? (
                <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>Sale/Scrap ID: <b>{saleScrapId}</b></div>
              ) : null}
            </div>
            <button type="button" onClick={() => navigate(-1)} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}>
              <FaArrowLeft /> Back
            </button>
          </div>

          <div className={`rounded-lg border p-4 ${isDark ? "border-gray-700 bg-gray-800/40" : "border-gray-200 bg-gray-50"}`}>
            {loading ? (
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
            ) : message ? (
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>{message}</div>
            ) : (
              <div className="space-y-2">
                {docs.map((d, idx) => (
                  <button
                    key={`${d.fileName}-${idx}`}
                    type="button"
                    onClick={() => openDoc(d)}
                    className={`w-full max-w-xl text-left px-3 py-2 rounded flex items-center gap-2 ${isDark ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                  >
                    <FaFolderOpen />
                    <span className="truncate">{d.fileName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className={`text-xs text-right mt-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>INFRA SCREEN : B-27/2</p>
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}